'use client';

import { LogoRegister, LogoSlogan, LogoText } from '@/components/common';
import { LogoLinks } from '@/components/common/smartguy-logo';
import AppFooter from '@/components/layouts/AppFooter';
import CommonNav from '@/components/layouts/CommonNav';
import { Skeleton } from '@/components/ui/skeleton';
import { TypingIndicator } from '@/app/(user)/chat/components/TypingIndicator';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { Suspense } from 'react';

function CategoryPillsSkeleton() {
  return (
    <div className="flex flex-wrap gap-2">
      {Array.from({ length: 8 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-32 rounded-full" />
      ))}
      <Skeleton className="h-10 w-10 rounded-full sm:h-11 sm:w-11" />
    </div>
  );
}

function SubCategorySkeleton() {
  return (
    <>
      <div className="mt-5 space-y-4">
        <Skeleton className="h-14 w-full max-w-md rounded-2xl" />
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-28 rounded-full" />
        ))}
      </div>
    </>
  );
}

function VideoBubbleSkeleton() {
  return (
    <div className="mt-5 space-y-4">
      <Skeleton className="h-14 w-full max-w-md rounded-2xl" />
    </div>
  );
}

function VideoGridSkeleton({ count = 8, cols = '2xl:grid-cols-4' }) {
  return (
    <div className={`mt-6 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 ${cols}`}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="space-y-3 rounded-lg border pb-3">
          <Skeleton className="h-[220px] w-full rounded-t-lg" />
          <div className="space-y-2 px-4">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

function PaginationSkeleton() {
  return (
    <div className="mt-5 flex items-center justify-center gap-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-20" />
      ))}
    </div>
  );
}

function NavSkeleton() {
  return <Skeleton className="h-10 w-full" />;
}

function LoadingBody() {
  const searchParams = useSearchParams();
  const category = searchParams.get('category');
  const subCategory = searchParams.get('subCategory');

  const isLibrary = !category;
  const isCategoryOnly = category && !subCategory;
  const isSubCategory = category && subCategory;

  return (
    <div className="mx-auto space-y-4">
      <CategoryPillsSkeleton />

      {isCategoryOnly && (
        <>
          <TypingIndicator />
          <SubCategorySkeleton />
        </>
      )}

      {isSubCategory && (
        <>
          <SubCategorySkeleton />
          <VideoBubbleSkeleton />
          <TypingIndicator />
          <VideoGridSkeleton count={6} cols="lg:grid-cols-3" />
        </>
      )}

      {isLibrary && (
        <>
          <VideoGridSkeleton count={8} />
          <PaginationSkeleton />
        </>
      )}
    </div>
  );
}

export default function VideosPageLoading() {
  return (
    <div className="h-full w-full">
      <div className="space-y-4">
        <div className="flex flex-1 flex-col items-center justify-center py-4">
          <Link href="/" className="flex flex-col items-center justify-center">
            <div className="flex items-center">
              <LogoText className="text-3xl" />
              <LogoRegister />
            </div>
            <LogoSlogan className="me-4 text-center text-[.7rem]" />
          </Link>
          <LogoLinks />
        </div>
        <div>
          <CommonNav />
        </div>
      </div>

      <div className="container mx-auto space-y-4">
        <div className="mx-auto w-full max-w-xs sm:max-w-md">
          <Skeleton className="mt-3 mb-6 h-14 w-full rounded-full" />
        </div>

        <Suspense
          fallback={
            <div className="mx-auto space-y-4">
              <CategoryPillsSkeleton />
              <TypingIndicator />
            </div>
          }
        >
          <LoadingBody />
        </Suspense>

        <AppFooter />
      </div>
    </div>
  );
}
